<template>
  <div class="markdown-block">
    <stream-markdown
      :content="block.text"
      mode="static"
      :completion="false"
      :parser-options="PARSER_OPTIONS"
      :harden-options="HARDEN_OPTIONS"
      :controls="false"
      :previewers="false"
      :extensions="EXTENSIONS"
      data-testid="ai-chat-markdown-content"
    />
  </div>
</template>

<script setup lang="ts">
import { Markdown as StreamMarkdown } from 'vue-stream-markdown'
import type {
  ComarkPlugin,
  MarkdownNode,
  StreamMarkdownProps
} from 'vue-stream-markdown'
import type { MarkdownBlock } from '../../types'

/* `text` is Markdown, re-rendered whole on every streamed delta */
defineProps<{
  block: MarkdownBlock
}>()

/* The text is model output that can echo injected tool results, so links keep only their text and images only their alt text */
const toPlainText = (nodes: MarkdownNode[]): MarkdownNode[] =>
  nodes.flatMap((node): MarkdownNode[] => {
    if (typeof node === 'string' || node[0] === null) return [node]
    const [tag, attributes, ...children] = node
    if (tag === 'img') return attributes.alt ? [String(attributes.alt)] : []
    if (tag === 'a') return toPlainText(children)
    return [[tag, attributes, ...toPlainText(children)]]
  })

const plainLinksAndImages: ComarkPlugin = {
  name: 'ai-chat-plain-links-and-images',
  post(state) {
    state.tree.nodes = toPlainText(state.tree.nodes)
  }
}

/* Without the default plugins comark leaves raw HTML, component and attribute syntax as text */
const PARSER_OPTIONS: StreamMarkdownProps['parserOptions'] = {
  registerDefaultPlugins: false,
  linkify: false,
  headingIds: false,
  plugins: [plainLinksAndImages]
}

/* Backstop if a link or image ever reaches the renderer: empty allowlists block every http(s) URL */
const HARDEN_OPTIONS: StreamMarkdownProps['hardenOptions'] = {
  allowedLinkPrefixes: [],
  allowedImagePrefixes: [],
  allowedProtocols: [],
  allowDataImages: false
}

const EXTENSIONS: StreamMarkdownProps['extensions'] = {
  code: false,
  math: false,
  mermaid: false,
  beautifulMermaid: false
}
</script>

<style scoped>
.markdown-block {
  align-self: flex-start;
  max-width: 88%;
  min-width: 0;
  padding: 0.5rem 0.75rem;
  border-radius: 0.25rem;
  border-bottom-left-radius: 0.1875rem;
  background: var(--el-fill-color);
  color: var(--el-text-color-primary);
  font-size: 0.875rem;
  line-height: 1.5;
  overflow-wrap: anywhere;
}

.markdown-block :deep(p),
.markdown-block :deep(ul),
.markdown-block :deep(ol),
.markdown-block :deep(pre),
.markdown-block :deep(table) {
  margin: 0 0 0.5rem;
}

.markdown-block :deep(:is(h1, h2, h3, h4, h5, h6)) {
  margin: 0.75rem 0 0.25rem;
  font-weight: 600;
  line-height: 1.3;
}

.markdown-block :deep(h1) {
  font-size: 1.125rem;
}

.markdown-block :deep(h2) {
  font-size: 1rem;
}

.markdown-block :deep(:is(h3, h4, h5, h6)) {
  font-size: 0.875rem;
}

.markdown-block :deep(:is(ul, ol)) {
  padding-left: 1.25rem;
}

/* soft line breaks inside a paragraph stay visible, as in the plain-text block before Markdown */
.markdown-block :deep([data-stream-markdown='text']) {
  white-space: pre-wrap;
}

.markdown-block :deep(ul) {
  list-style: disc;
}

/* ordered items carry their own `1.` marker element, so the native marker is turned off */
.markdown-block :deep(ol) {
  padding-left: 0.25rem;
  list-style: none;
}

.markdown-block :deep(ol > li) {
  display: flex;
  gap: 0.25rem;
}

.markdown-block :deep(li > :is(ul, ol)) {
  margin-bottom: 0;
}

.markdown-block :deep(code) {
  padding: 0.0625rem 0.25rem;
  border-radius: 0.1875rem;
  background: var(--el-fill-color-darker);
  font-family: var(--el-font-family-mono, monospace);
  font-size: 0.8125rem;
}

.markdown-block :deep(pre) {
  padding: 0.5rem;
  border-radius: 0.25rem;
  background: var(--el-fill-color-darker);
  overflow-x: auto;
  white-space: pre;
}

.markdown-block :deep(pre code) {
  padding: 0;
  background: none;
}

.markdown-block :deep([data-stream-markdown='code-block-header']) {
  display: none;
}

.markdown-block :deep(li > p) {
  margin: 0 !important;
}

/* `display: block` lets a table wider than the drawer scroll inside the bubble */
.markdown-block :deep([data-stream-markdown='table-inner-wrapper'] > table) {
  width: fit-content !important;
  border-radius: 0 !important;
}

/* `display: block` lets a table wider than the drawer scroll inside the bubble */
.markdown-block :deep(table) {
  display: block;
  border-collapse: collapse;
  font-size: 0.75rem;
}

.markdown-block :deep(:is(th, td)) {
  padding: 0.25rem 0.5rem;
  border: 1px solid var(--el-border-color);
  text-align: left;
  white-space: nowrap;
}

.markdown-block :deep(th) {
  background: var(--el-fill-color-light);
  font-weight: 600;
}

.markdown-block :deep(> :last-child > :last-child) {
  margin-bottom: 0;
}
</style>
